import { getRouteApi, Link } from '@tanstack/react-router';
import { ArrowLeftIcon, CopyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useApiOrdersIdGet } from '@r/api/generated/client.ts';
import { Card, CardContent, CardHeader } from '@r/components/ui/card.tsx';
import { FormattedHtml } from '@r/components/formatted-html.tsx';
import { Button } from '@ui/button.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import shopDefaultBanner from '@r/assets/images/fiddy/default-shop-banner.png';
import { cn } from '@r/utils/utils.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import type { ReactNode } from 'react';

const orderStatusStyles = {
  'order.status.pending': 'bg-warning/20 text-warning border-warning/30',
  'order.status.confirmed': 'bg-info/20 text-info border-info/30',
  'order.status.completed': 'bg-success/20 text-success border-success/30',
  'order.status.canceled': 'bg-muted text-muted-foreground border-muted-foreground/10',
  'order.status.failed': 'bg-destructive/20 text-destructive border-destructive/30'
} as const;

const getOrderStatusStyle = (status?: string) => {
  const normalizedStatus = status?.trim().toLowerCase() as
    | keyof typeof orderStatusStyles
    | undefined;
  if (normalizedStatus && orderStatusStyles[normalizedStatus]) {
    return orderStatusStyles[normalizedStatus];
  }
  return 'bg-muted text-muted-foreground border-muted-foreground/10';
};

export const OrderRewardDetail = () => {
  const { t } = useTranslation();
  const user = useUser().data;
  const workspaces = UseWorkspace(user);
  const routeApi = getRouteApi('/auth-layout/shops/orders/$id/reward-obtained/$productId');
  const { id: orderId, productId } = routeApi.useParams();

  const orderQuery = useApiOrdersIdGet(orderId, {
    query: {
      queryKey: ['order_id', workspaces.workspace?.id, user?.id, orderId, productId],
      enabled: !!workspaces.workspace?.id
    }
  });
  useOrvalQueryErrorToast(orderQuery, t('toast.error.label'));

  const { data: order, isPending, isError } = orderQuery;

  const selectedOrderProduct = order?.orderProducts.find(
    (orderProduct) => String(orderProduct.product.id) === String(productId)
  );
  const product = selectedOrderProduct?.product;
  const publicMetadatas = product?.publicMetadatas ?? [];

  const getMetadataValue = (metaKey: string) =>
    publicMetadatas.find((meta) => meta.metaKey === metaKey)?.metaValue;

  const orderProductCode = selectedOrderProduct?.orderProductCode;
  const activationCode = orderProductCode?.code ?? '-';
  const codeExpiredAt = orderProductCode?.expiredAt;
  const codeDetail = getMetadataValue('codeDetail') ?? '-';

  const handleCopyActivationCode = () => {
    if (!activationCode || activationCode === '-') return;
    navigator.clipboard
      .writeText(activationCode)
      .then(() => toast.success(t('common.clipboard.success')))
      .catch(() => toast.error(t('common.clipboard.error')));
  };

  const InfoLine = (label: string, value: ReactNode) => (
    <div className="flex flex-col col-span-12 md:col-span-6 lg:col-span-12 xl:col-span-6 h-fit">
      <span className="text-muted-foreground uppercase text-sm font-medium">{t(label)}</span>
      <span className="font-medium">{value}</span>
    </div>
  );

  const StatusBadge = () => (
    <div
      className={cn(
        'inline-flex gap-2 justify-center items-center w-fit',
        'px-3.5 py-1 text-sm font-medium whitespace-nowrap rounded-full',
        getOrderStatusStyle(order?.status)
      )}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
      {order?.status ? t(order.status) : '-'}
    </div>
  );

  return (
    <div className="space-y-5">
      <Link
        to="/shops/orders/$id"
        params={{ id: orderId }}
        className="inline-flex gap-2 items-center text-sm font-medium text-primary hover:underline"
      >
        <Button rounded="full">
          <ArrowLeftIcon />
          {t('common.back')}
        </Button>
      </Link>

      <Card className="w-full overflow-hidden">
        <CardHeader className="px-0 pt-0">
          <Card className="grid grid-cols-12 overflow-hidden h-fit rounded-none border-0 border-b">
            <div className="aspect-square sm:aspect-auto col-span-12 sm:col-span-5 lg:col-span-6 xl:col-span-3 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-l from-muted-foreground/10 to-transparent to-50% z-10" />
              {isPending ? (
                <Skeleton className="absolute inset-0 w-full h-full rounded-none" />
              ) : (
                <img
                  className="scale-75 sm:scale-100 absolute inset-0 w-full h-full object-contain object-center"
                  src={product?.images?.[0]?.pathUri || shopDefaultBanner}
                  alt={product?.name}
                  onError={(e) => {
                    e.currentTarget.src = shopDefaultBanner;
                    e.currentTarget.onerror = null;
                  }}
                />
              )}
            </div>

            <div className="col-span-12 sm:col-span-7 lg:col-span-6 xl:col-span-9 p-6 grid grid-cols-12 gap-5 h-fit">
              {isPending ? (
                <>
                  <Skeleton className="col-span-12 h-6 w-48" />
                  <Skeleton className="col-span-12 md:col-span-6 h-10 w-64" />
                  <Skeleton className="col-span-12 md:col-span-6 h-10 w-40" />
                  <Skeleton className="col-span-12 md:col-span-6 h-10 w-56" />
                </>
              ) : (
                <>
                  {/* Commande */}
                  <p className="col-span-12 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('admin.order.detail.table.title')}
                  </p>

                  {InfoLine('order.reference.short', order?.reference ?? orderId)}

                  <div className="flex flex-col col-span-12 md:col-span-6 lg:col-span-12 xl:col-span-6 h-fit">
                    <span className="text-muted-foreground uppercase text-sm font-medium">
                      {t('label.order.status')}
                    </span>

                    <StatusBadge />
                  </div>

                  <div className="col-span-12 h-px bg-border" />

                  {/* Article */}
                  <p className="col-span-12 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('admin.order.reward.detail.title')}
                  </p>

                  {InfoLine(
                    'order.item',
                    <span className="flex flex-col">
                      <span>{product?.name}</span>
                      {product?.designationCom && (
                        <span className="text-sm text-muted-foreground font-normal">
                          {product.designationCom}
                        </span>
                      )}
                    </span>
                  )}

                  <div className="flex flex-col col-span-12 md:col-span-6 lg:col-span-12 xl:col-span-6 break-all h-fit">
                    <span className="text-muted-foreground uppercase text-sm font-medium">
                      {t('admin.order.reward.detail.product.reference.prefix')}
                    </span>
                    <span className="font-medium">{product?.id ?? '-'}</span>
                  </div>

                  {product?.ean && InfoLine('shop.product.ean.label', product.ean)}

                  {product?.isDemat && (
                    <div className="flex flex-col col-span-12 md:col-span-6 lg:col-span-12 xl:col-span-6 h-fit">
                      <span className="text-muted-foreground uppercase text-sm font-medium">
                        {t('admin.order.reward.detail.activation.code')}
                      </span>
                      <div className="inline-flex gap-2 items-center font-medium">
                        <span>{activationCode}</span>
                        {activationCode !== '-' && (
                          <Button
                            size="icon"
                            variant="raw"
                            type="button"
                            className="text-muted-foreground h-5 w-5"
                            onClick={handleCopyActivationCode}
                          >
                            <CopyIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      {codeExpiredAt && (
                        <span className="text-xs text-muted-foreground">
                          {t('admin.order.reward.detail.activation.code.expires.at', {
                            date: new Date(codeExpiredAt).toLocaleDateString()
                          })}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </CardHeader>

        <CardContent className="p-6 pt-0">
          {isPending ? (
            <div className="space-y-3">
              <Skeleton className="w-32 h-5" />
              <Skeleton className="w-full h-16" />
            </div>
          ) : isError || !selectedOrderProduct || !product ? (
            <p className="text-sm text-muted-foreground">
              {t('admin.order.reward.detail.not.found')}
            </p>
          ) : (
            <div className="space-y-4">
              {product.description ? (
                <FormattedHtml
                  as="div"
                  text={product.description}
                  className="text-muted-foreground"
                />
              ) : (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground uppercase">
                    {t('admin.order.reward.detail.legal.notice')}
                  </p>
                  <p className="text-muted-foreground">{codeDetail}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
